// src/pages/stations/StationForm.js
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  TextField,
  Button,
  Typography,
  Select,
  MenuItem,
  FormHelperText,
  Box,
  Checkbox,
} from '@mui/material';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import stationService from '../../services/stationService';
import eventService from '../../services/eventService';
import stationTypeService from '../../services/stationTypeService';
import { isEmpty, map, toUpper } from 'lodash';
import LayoutContainer from '../../components/LayoutContainer';
import AdminHeader from '../../components/AdminHeader';
import itemTypeService from '../../services/itemTypeService';

const StationForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [stationTypes, setStationTypes] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [initialValues, setInitialValues] = useState({
    type: '',
    event_id: '',
    event_section_id: '',
    station_type_id: '',
  });

  useEffect(() => {
    const fetchStation = async () => {
      if (id) {
        const response = await stationService.getStation(id);
        setInitialValues(response.data);
      }
    };
    fetchStation();
  }, [id]);

  const fetchProductTypes = async () => {
    const response = await itemTypeService.getItemTypes();
    setProductTypes(response?.data);
  };

  const stationList = async () => {
    const response = await stationTypeService.getStationTypes();
    setStationTypes(response.data);
  };

  const fetchEvents = async () => {
    const response = await eventService.getEvents();
    setEvents(response.data);
  };

  useEffect(() => {
    stationList();
    fetchEvents();
    fetchProductTypes();
  }, []);

  const validationSchema = Yup.object({
    station_name: Yup.string().required('Station name is required'),
    station_type_id: Yup.string().required('Station type is required'),
    latitude: Yup.number()
      .required('Latitude is required')
      .typeError('Latitude must be a number'),
    longitude: Yup.number()
      .required('Longitude is required')
      .typeError('Longitude must be a number'),
    event_id: Yup.string().required('Event is required'),
  });

  return (
    <LayoutContainer>
      <AdminHeader
        title={id ? 'Edit Station' : 'Add Station'}
        backTo='/stations'
      />
      <div className='page-content-wrapper scrollable'>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          enableReinitialize
          onSubmit={async (values) => {
            if (id) {
              await stationService.updateStation(id, values);
            } else {
              await stationService.createStation(values);
            }
            navigate('/stations');
          }}
        >
          {({
            values,
            handleChange,
            handleBlur,
            setValues,
            touched,
            errors,
            setFieldValue,
          }) => (
            <Form>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                <div
                  style={{ color: '#000', fontSize: '0.87rem', marginTop: 8 }}
                >
                  Station Name
                </div>
                <TextField
                  name='station_name'
                  fullWidth
                  value={values?.station_name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <ErrorMessage
                  name='station_name'
                  component={FormHelperText}
                  style={{ color: '#D32F2F' }}
                />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                <div
                  style={{ color: '#000', fontSize: '0.87rem', marginTop: 8 }}
                >
                  Station Type
                </div>
                <Select
                  name='station_type_id'
                  value={values?.station_type_id}
                  fullWidth
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  {!isEmpty(stationTypes) ? (
                    map(stationTypes, (option) => (
                      <MenuItem key={option?.id} value={option?.id}>
                        {option?.type}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem>No data found</MenuItem>
                  )}
                </Select>
                <ErrorMessage
                  name='station_type_id'
                  component={FormHelperText}
                  style={{ color: '#D32F2F' }}
                />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                <div
                  style={{ color: '#000', fontSize: '0.87rem', marginTop: 8 }}
                >
                  Latitude
                </div>
                <Field
                  as={TextField}
                  name='latitude'
                  fullWidth
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <ErrorMessage
                  name='latitude'
                  component={FormHelperText}
                  style={{ color: '#D32F2F' }}
                />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                <div
                  style={{ color: '#000', fontSize: '0.87rem', marginTop: 8 }}
                >
                  Longitude
                </div>
                <Field
                  as={TextField}
                  name='longitude'
                  fullWidth
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <ErrorMessage
                  name='longitude'
                  component={FormHelperText}
                  style={{ color: '#D32F2F' }}
                />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                <div
                  style={{ color: '#000', fontSize: '0.87rem', marginTop: 8 }}
                >
                  Event
                </div>
                <Field
                  as={Select}
                  name='event_id'
                  fullWidth
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  {!isEmpty(events) ? (
                    map(events, (option) => (
                      <MenuItem key={option?.id} value={option?.id}>
                        {option?.name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem>No data found</MenuItem>
                  )}
                </Field>
                <ErrorMessage
                  name='event_id'
                  component={FormHelperText}
                  style={{ color: '#D32F2F' }}
                />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                <Typography variant='body2' sx={{ fontWeight: 600 }}>
                  Product Types
                </Typography>
                <TextField
                  select
                  name='item_type_id'
                  fullWidth
                  value={values?.item_type_id || []}
                  onChange={(e) => {
                    const {
                      target: { value },
                    } = e;
                    setFieldValue(
                      'item_type_id',
                      typeof value === 'string' ? value.split(',') : value
                    );
                    handleChange(e);
                  }}
                  onBlur={handleBlur}
                  sx={{
                    backgroundColor: '#f5f5f5',
                    borderRadius: 1,
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor:
                          touched.item_type_id && errors.item_type_id
                            ? '#d32f2f'
                            : '#ccc',
                      },
                    },
                  }}
                  SelectProps={{
                    multiple: true,
                    renderValue: (selected) => selected.join(', '), // Display selected roles as a comma-separated string
                    // MenuProps: {
                    //   ...menuBox,
                    // },
                  }}
                >
                  {productTypes?.map((type) => (
                    <MenuItem key={type.type} value={type.type}>
                      <Checkbox
                        checked={values.item_type_id?.includes(type.type)}
                        sx={{ padding: 0, marginRight: '8px' }}
                      />
                      {toUpper(type.type)}
                    </MenuItem>
                  ))}
                </TextField>
                {touched.item_type_id && errors.item_type_id && (
                  <FormHelperText error>{errors.item_type_id}</FormHelperText>
                )}
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  type='submit'
                  variant='contained'
                  sx={{ backgroundColor: '#be210b', textTransform: 'none' }}
                >
                  {id ? 'Update' : 'Create'}
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      </div>
    </LayoutContainer>
  );
};

export default StationForm;
